import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton,
    Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Switch } from '../../components/switch/Switch.tsx';
import {
    attractionsService, type AdminProduct, type AttractionCollection, type CollectionBody,
} from '../../services/attractions/attractionsService.ts';
import { AttractionsFrame, COLORS, Panel, errorText, ruleCleared } from './attractionsShared.tsx';

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const slugify = (title: string): string => title
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\{city\}/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const bodyOf = (c: AttractionCollection): CollectionBody => ({
    slug: c.slug, title: c.title, titleHe: c.titleHe, subtitle: c.subtitle, subtitleHe: c.subtitleHe, sortOrder: c.sortOrder, active: c.active,
});

/**
 * Product titles by code, from the admin catalogue of every city. The contract
 * has no lookup by code, so the catalogue is read once per page visit, city
 * by city, the first time a collection's products are opened.
 */
let productIndex: Promise<Map<string, AdminProduct & { cityName: string }>> | null = null;
const loadProductIndex = (): Promise<Map<string, AdminProduct & { cityName: string }>> => {
    if (!productIndex) {
        productIndex = (async () => {
            const map = new Map<string, AdminProduct & { cityName: string }>();
            const first = await attractionsService.catalogue({ filter: 'all' });
            const add = (cityId: number | null, groups: typeof first.groups) => {
                const cityName = first.cities.find(c => c.cityId === cityId)?.name ?? '';
                groups.forEach(g => g.products.forEach(p => map.set(p.code, { ...p, cityName })));
            };
            add(first.cityId, first.groups);
            const others = first.cities.filter(c => c.cityId !== first.cityId);
            const rest = await Promise.all(others.map(c => attractionsService.catalogue({ cityId: c.cityId, filter: 'all' }).catch(() => null)));
            rest.forEach(r => r && add(r.cityId, r.groups));
            return map;
        })().catch(e => { productIndex = null; throw e; });
    }
    return productIndex;
};

// ---------------------------------------------------------------- editor

type Draft = { title: string; titleHe: string; subtitle: string; subtitleHe: string; slug: string; sortOrder: string; active: boolean };

const draftOf = (c: AttractionCollection | null, nextOrder: number): Draft => ({
    title: c?.title ?? '',
    titleHe: c?.titleHe ?? '',
    subtitle: c?.subtitle ?? '',
    subtitleHe: c?.subtitleHe ?? '',
    slug: c?.slug ?? '',
    sortOrder: String(c?.sortOrder ?? nextOrder),
    active: c?.active ?? true,
});

const CollectionEditor: React.FC<{
    collection: AttractionCollection | null;
    nextOrder: number;
    onClose: () => void;
    onSaved: (c: AttractionCollection, created: boolean) => void;
}> = ({ collection, nextOrder, onClose, onSaved }) => {
    const [d, setD] = useState<Draft>(() => draftOf(collection, nextOrder));
    const [slugTouched, setSlugTouched] = useState(!!collection);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setD(prev => {
            const next = { ...prev, [k]: value };
            if (k === 'title' && !slugTouched) next.slug = slugify(value);
            return next;
        });
        if (k === 'slug') setSlugTouched(true);
    };

    const slugBad = d.slug !== '' && !SLUG.test(d.slug);
    const orderBad = d.sortOrder.trim() !== '' && !/^-?\d+$/.test(d.sortOrder.trim());
    const valid = d.title.trim() !== '' && d.slug !== '' && !slugBad && !orderBad;

    const save = async () => {
        if (!valid) return;
        setSaving(true);
        setError(null);
        const body: CollectionBody = {
            slug: d.slug.trim(),
            title: d.title.trim(),
            titleHe: d.titleHe.trim() || null,
            subtitle: d.subtitle.trim() || null,
            subtitleHe: d.subtitleHe.trim() || null,
            sortOrder: d.sortOrder.trim() === '' ? 0 : Number(d.sortOrder),
            active: d.active,
        };
        try {
            const stored = collection
                ? await attractionsService.updateCollection(collection.id, body)
                : await attractionsService.createCollection(body);
            onSaved(stored, !collection);
        } catch (e: any) {
            setError(e?.response?.data?.code === 'error.attraction_collection_slug_taken'
                ? errorText(e, 'Another collection already uses this slug')
                : errorText(e, 'Could not save the collection'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontWeight: 700 }}>{collection ? 'Edit collection' : 'New collection'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <TextField size="small" label="Title" required value={d.title} onChange={set('title')} autoFocus
                            helperText="{city} is replaced by the city name, e.g. “Half-day from {city}”" />
                        <TextField size="small" label="Title (Hebrew)" value={d.titleHe} onChange={set('titleHe')} inputProps={{ dir: 'rtl' }} />
                        <TextField size="small" label="Subtitle" value={d.subtitle} onChange={set('subtitle')} />
                        <TextField size="small" label="Subtitle (Hebrew)" value={d.subtitleHe} onChange={set('subtitleHe')} inputProps={{ dir: 'rtl' }} />
                        <TextField size="small" label="Slug" required value={d.slug} onChange={set('slug')} error={slugBad}
                            helperText={slugBad ? 'Lower-case letters, digits and dashes only' : 'Used in the app’s links, e.g. best-for-kids'} />
                        <TextField size="small" label="Sort order" value={d.sortOrder} onChange={set('sortOrder')} error={orderBad}
                            inputProps={{ inputMode: 'numeric' }} helperText={orderBad ? 'A whole number' : 'Lower comes first in the app'} />
                    </Box>
                    <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}>
                        <Switch size="medium" checked={d.active} onChange={(_: any, checked: boolean) => setD({ ...d, active: checked })} />
                        <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Active</Typography>
                            <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>Shown in the app when it has at least one product in app</Typography>
                        </Box>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving} sx={{ color: COLORS.text }}>Cancel</Button>
                <Button variant="contained" color="secondary" onClick={save} disabled={!valid || saving} sx={{ color: '#fff', borderRadius: '20px', px: 2.5 }}>
                    {saving ? 'Saving…' : collection ? 'Save' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ---------------------------------------------------------------- one collection

const CollectionRow: React.FC<{
    c: AttractionCollection;
    onChanged: (c: AttractionCollection) => void;
    onEdit: () => void;
    onDelete: () => void;
}> = ({ c, onChanged, onEdit, onDelete }) => {
    const notify = useNotify();
    const [open, setOpen] = useState(false);
    const [index, setIndex] = useState<Map<string, AdminProduct & { cityName: string }> | null>(null);
    const [indexFailed, setIndexFailed] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);

    useEffect(() => {
        if (!open || index) return;
        let cancelled = false;
        loadProductIndex()
            .then(m => { if (!cancelled) setIndex(m); })
            .catch(() => { if (!cancelled) setIndexFailed(true); });
        return () => { cancelled = true; };
    }, [open, index]);

    const toggleActive = async (active: boolean) => {
        setBusy('active');
        try {
            onChanged(await attractionsService.updateCollection(c.id, { ...bodyOf(c), active }));
        } catch (e) {
            notify(errorText(e, 'Could not change the collection'), { type: 'error' });
        } finally {
            setBusy(null);
        }
    };

    const remove = async (code: string) => {
        setBusy(code);
        try {
            onChanged(await attractionsService.removeFromCollection(c.id, code));
        } catch (e) {
            notify(errorText(e, 'Could not remove the product'), { type: 'error' });
        } finally {
            setBusy(null);
        }
    };

    const codes = c.productCodes ?? [];

    return (
        <Box sx={{ borderBottom: `1px solid ${COLORS.rowBorder}`, '&:last-of-type': { borderBottom: 0 } }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 2fr) 1fr 150px 90px 80px 100px', gap: 1.5, px: 2.25, py: 1.5, alignItems: 'center', minWidth: 760 }}>
                <Box sx={{ minWidth: 0, opacity: c.active ? 1 : 0.6 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{c.title}</Typography>
                    {c.subtitle && <Typography sx={{ fontSize: 12.5, color: COLORS.muted, mt: '2px' }}>{c.subtitle}</Typography>}
                    {c.titleHe && <Typography dir="rtl" sx={{ fontSize: 12, color: COLORS.muted, mt: '2px', textAlign: 'start' }}>{c.titleHe}</Typography>}
                </Box>
                <Typography sx={{ fontSize: 13, color: COLORS.body, fontFamily: 'ui-monospace, Menlo, monospace', overflowWrap: 'anywhere' }}>{c.slug}</Typography>
                <Button size="small" onClick={() => setOpen(!open)} endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ justifyContent: 'flex-start', color: COLORS.tealDark, fontWeight: 700, px: 1, whiteSpace: 'nowrap', justifySelf: 'start' }}>
                    {c.productCount ?? codes.length} {(c.productCount ?? codes.length) === 1 ? 'product' : 'products'}
                </Button>
                <Typography sx={{ fontSize: 13, color: COLORS.body, fontVariantNumeric: 'tabular-nums' }}>{c.sortOrder}</Typography>
                <Switch size="medium" checked={c.active} disabled={busy === 'active'}
                    inputProps={{ 'aria-label': `Active: ${c.title}` }} onChange={(_: any, checked: boolean) => toggleActive(checked)} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Tooltip title="Edit"><IconButton size="small" onClick={onEdit} aria-label={`Edit ${c.title}`}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" onClick={onDelete} aria-label={`Delete ${c.title}`}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
            </Box>
            {open && (
                <Box sx={{ px: 2.25, pb: 1.75, pt: 0.25, bgcolor: '#FAFBFB', borderTop: `1px solid ${COLORS.rowBorder}` }}>
                    {codes.length === 0 && (
                        <Typography sx={{ fontSize: 13, color: COLORS.muted, pt: 1.25 }}>
                            No products yet. Add them from the catalogue with the “+” in the Collections column.
                        </Typography>
                    )}
                    {codes.length > 0 && !index && !indexFailed && <CircularProgress size={18} sx={{ mt: 1.25 }} />}
                    {codes.length > 0 && (index || indexFailed) && codes.map(code => {
                        const p = index?.get(code);
                        return (
                            <Box key={code} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: `1px solid ${COLORS.rowBorder}`, '&:last-of-type': { borderBottom: 0 } }}>
                                <Box sx={{ width: 40, height: 30, borderRadius: '5px', bgcolor: '#D7DEDB', overflow: 'hidden', flexShrink: 0 }}>
                                    {p?.image?.url && <img src={p.image.url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text }} noWrap>{p?.title ?? code}</Typography>
                                    <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
                                        {[code, p?.cityName, p ? (p.inApp && ruleCleared(p) ? 'In app' : 'Not in app') : 'not in the catalogue'].filter(Boolean).join(' · ')}
                                    </Typography>
                                </Box>
                                <Tooltip title="Remove from this collection">
                                    <span>
                                        <IconButton size="small" disabled={busy === code} onClick={() => remove(code)} aria-label={`Remove ${p?.title ?? code}`}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        );
                    })}
                    {indexFailed && <Typography sx={{ fontSize: 12, color: COLORS.warn, mt: 0.75 }}>Could not load the product titles; showing codes.</Typography>}
                </Box>
            )}
        </Box>
    );
};

// ---------------------------------------------------------------- page

/** Attractions › Collections: Vello's own tags for In-app products ("Best for kids", "Half-day"...). */
const AttractionsCollectionsPage = () => {
    const notify = useNotify();
    const [list, setList] = useState<AttractionCollection[] | null>(null);
    const [failed, setFailed] = useState<string | null>(null);
    const [editing, setEditing] = useState<AttractionCollection | 'new' | null>(null);
    const [deleting, setDeleting] = useState<AttractionCollection | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            setList(await attractionsService.collections());
            setFailed(null);
        } catch (e) {
            setFailed(errorText(e, 'Could not load the collections. Reload the page to try again.'));
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    // Titles are read fresh on each visit to the page.
    useEffect(() => { productIndex = null; }, []);

    const sorted = (list ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
    const replace = (c: AttractionCollection) => setList(l => (l ?? []).map(x => (x.id === c.id ? c : x)));
    const nextOrder = sorted.length ? Math.max(...sorted.map(c => c.sortOrder)) + 1 : 0;

    const confirmDelete = async () => {
        if (!deleting) return;
        setDeleteBusy(true);
        try {
            await attractionsService.deleteCollection(deleting.id);
            setList(l => (l ?? []).filter(x => x.id !== deleting.id));
            notify(`Deleted “${deleting.title}”`, { type: 'success' });
            setDeleting(null);
        } catch (e) {
            notify(errorText(e, 'Could not delete the collection'), { type: 'error' });
        } finally {
            setDeleteBusy(false);
        }
    };

    return (
        <AttractionsFrame
            title="Collections"
            subtitle="Vello’s own groups of attractions, shown as rows in the app"
            actions={(
                <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setEditing('new')}
                    sx={{ borderRadius: '20px', color: '#fff', height: 40, px: 2.25 }}>
                    New collection
                </Button>
            )}
        >
            {failed && <Alert severity="error" sx={{ maxWidth: 760 }}>{failed}</Alert>}
            {!failed && list === null && <CircularProgress size={24} />}
            {list && list.length === 0 && (
                <Box sx={{ bgcolor: '#fff', border: '1px dashed #D1D5DB', borderRadius: '12px', py: 6, px: 3, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.body }}>No collections yet</Typography>
                    <Typography sx={{ fontSize: 13.5, color: COLORS.muted, mt: 0.75 }}>Create one, then tag products with it from the catalogue.</Typography>
                </Box>
            )}
            {list && list.length > 0 && (
                <Panel sx={{ overflow: 'hidden' }}>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{
                            display: 'grid', gridTemplateColumns: 'minmax(220px, 2fr) 1fr 150px 90px 80px 100px', gap: 1.5, px: 2.25, py: 1, minWidth: 760,
                            fontSize: 11.5, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '.4px',
                            borderBottom: `1px solid ${COLORS.rowBorder}`, bgcolor: '#FAFBFB',
                        }}>
                            <span>Collection</span><span>Slug</span><span>Products</span><span>Sort order</span><span>Active</span><span />
                        </Box>
                        {sorted.map(c => (
                            <CollectionRow key={c.id} c={c} onChanged={replace} onEdit={() => setEditing(c)} onDelete={() => setDeleting(c)} />
                        ))}
                    </Box>
                </Panel>
            )}

            {editing && (
                <CollectionEditor
                    collection={editing === 'new' ? null : editing}
                    nextOrder={nextOrder}
                    onClose={() => setEditing(null)}
                    onSaved={(c, created) => {
                        setList(l => (created ? [...(l ?? []), c] : (l ?? []).map(x => (x.id === c.id ? c : x))));
                        setEditing(null);
                        notify(created ? `Created “${c.title}”` : `Saved “${c.title}”`, { type: 'success' });
                    }}
                />
            )}

            {deleting && (
                <Dialog open onClose={deleteBusy ? undefined : () => setDeleting(null)} PaperProps={{ sx: { borderRadius: '16px' } }}>
                    <DialogTitle sx={{ fontWeight: 700 }}>Delete “{deleting.title}”?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            The collection disappears from the app. Its {deleting.productCount ?? deleting.productCodes?.length ?? 0} products stay in the catalogue and keep their In app switch.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setDeleting(null)} disabled={deleteBusy} sx={{ color: COLORS.text }}>Cancel</Button>
                        <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleteBusy} sx={{ borderRadius: '20px', px: 2.5 }}>
                            {deleteBusy ? 'Deleting…' : 'Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </AttractionsFrame>
    );
};

export default AttractionsCollectionsPage;
